import { forwardRef, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styles from './ScoopDialog.module.scss'
import { XIcon } from '@phosphor-icons/react'

// Track how many dialogs currently need body scroll lock.
// Only restore body overflow when the last dialog closes.
let scrollLockCount = 0

/**
 * ScoopDialog - High-performance modal dialog component
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} [props.open=false] - Whether the dialog is open
 * @param {Function} [props.onClose] - Callback when dialog should close
 * @param {string} [props.title] - Dialog title text
 * @param {React.ReactNode} [props.icon] - Optional icon to display before title
 * @param {React.ReactNode} props.children - Dialog content
 * @param {React.ReactNode} [props.actions] - Footer actions (buttons)
 * @param {boolean} [props.showCloseButton=true] - Show close button in header
 * @param {boolean} [props.extraWide=false] - Use extra wide layout (80% width, max 1500px)
 * @param {'sm' | 'md' | 'lg' | 'xl'} [props.maxWidth='sm'] - Maximum width preset
 * @param {boolean} [props.hasBackdrop=true] - Show backdrop overlay
 * @param {boolean} [props.closeOnBackdrop=true] - Close dialog when clicking backdrop
 * @param {boolean} [props.closeOnEscape=true] - Close dialog when pressing ESC
 * @param {string} [props.className] - Additional CSS classes for dialog content
 * @param {Object} [props.style] - Inline styles for dialog paper
 * @param {Object} [props.contentStyle] - Inline styles for dialog content
 * @param {Object} [props.containerStyle] - Inline styles for dialog container
 * @param {number} [props.zIndex=1400] - Z-index for the dialog
 * @param {number|string} [props.height] - Custom height for dialog (removes default maxHeight)
 * @param {React.Ref} ref - Forwarded ref to dialog container
 *
 * @example
 * // Basic dialog
 * <ScoopDialog open={isOpen} onClose={() => setIsOpen(false)} title="My Dialog">
 *   <div>Dialog content</div>
 * </ScoopDialog>
 *
 * @example
 * // Dialog with actions
 * <ScoopDialog
 *   open={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   title="Confirm"
 *   actions={
 *     <>
 *       <ScoopButton onClick={handleCancel}>Cancel</ScoopButton>
 *       <ScoopButton onClick={handleConfirm}>Confirm</ScoopButton>
 *     </>
 *   }
 * >
 *   <div>Are you sure?</div>
 * </ScoopDialog>
 */
export const ScoopDialog = forwardRef(
    (
        {
            open = false,
            onClose,
            title,
            icon,
            children,
            actions,
            showCloseButton = true,
            extraWide = false,
            maxWidth = 'sm',
            hasBackdrop = true,
            closeOnBackdrop = true,
            closeOnEscape = true,
            className = '',
            style,
            contentStyle,
            containerStyle,
            zIndex = 1600,
            height,
            ...props
        },
        ref
    ) => {
        const [isAnimating, setIsAnimating] = useState(false)
        const [shouldRender, setShouldRender] = useState(open)
        const dialogRef = useRef(null)

        // Handle opening/closing animations
        useEffect(() => {
            if (open) {
                setShouldRender(true)
                // Small delay to trigger animation after render
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        setIsAnimating(true)
                    })
                })
            } else {
                setIsAnimating(false)
                // Wait for animation to complete before unmounting
                const timer = setTimeout(() => {
                    setShouldRender(false)
                }, 225) // Match animation duration
                return () => clearTimeout(timer)
            }
        }, [open])

        // Handle ESC key
        useEffect(() => {
            if (!open || !closeOnEscape) return

            const handleEscape = (event) => {
                if (event.key === 'Escape') {
                    onClose?.()
                }
            }

            document.addEventListener('keydown', handleEscape)
            return () => document.removeEventListener('keydown', handleEscape)
        }, [open, onClose, closeOnEscape])

        // Prevent body scroll when dialog is open (reference-counted)
        useEffect(() => {
            if (open) {
                scrollLockCount++
                if (scrollLockCount === 1) {
                    document.body.style.overflow = 'hidden'
                }
                return () => {
                    scrollLockCount--
                    if (scrollLockCount === 0) {
                        document.body.style.overflow = ''
                    }
                }
            }
        }, [open])

        const handleBackdropClick = (event) => {
            if (closeOnBackdrop && event.target === event.currentTarget) {
                onClose?.()
            }
        }

        // Don't render if not open
        if (!shouldRender) return null

        const backdropClassName = [styles.backdrop, isAnimating && styles.backdropVisible]
            .filter(Boolean)
            .join(' ')

        const dialogClassName = [
            styles.dialog,
            extraWide ? styles.extraWide : styles[maxWidth],
            isAnimating && styles.open,
            className,
        ]
            .filter(Boolean)
            .join(' ')

        const dialogStyle = {
            // '--dialog-z-index': zIndex,
            ...(height && {
                height: typeof height === 'number' ? `${height}px` : height,
            }),
            ...style,
        }

        const dialogContent = (
            <div className={styles.dialogContainer} style={{ zIndex }}>
                {hasBackdrop && <div className={backdropClassName} onClick={handleBackdropClick} />}
                <div
                    ref={ref || dialogRef}
                    className={dialogClassName}
                    style={dialogStyle}
                    role="dialog"
                    aria-modal="true"
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    {...props}
                >
                    <div className={styles.dialogContentContainer} style={containerStyle}>
                        {/* Header with title and close button */}
                        {icon ? (
                            <div className={styles.dialogHeaderWithIcon}>
                                {typeof icon === 'string' ? (
                                    <img src={icon} alt="Icon" className={styles.dialogIcon} />
                                ) : (
                                    <div className={styles.dialogIcon}>{icon}</div>
                                )}
                                <div className={styles.dialogTitle}>{title}</div>
                                {showCloseButton && (
                                    <div onClick={onClose} className={styles.dialogXButtonWImage}>
                                        <XIcon size={20} weight="regular" />
                                    </div>
                                )}
                            </div>
                        ) : title ? (
                            <div className={styles.dialogHeader}>
                                <div className={styles.dialogTitle}>{title}</div>
                                {showCloseButton && (
                                    <div onClick={onClose} className={styles.dialogXButton}>
                                        <XIcon size={20} weight="regular" />
                                    </div>
                                )}
                            </div>
                        ) : (
                            showCloseButton && (
                                <div className={styles.closeButtonContainer}>
                                    <div onClick={onClose} className={styles.dialogXButton}>
                                        <XIcon size={20} weight="regular" />
                                    </div>
                                </div>
                            )
                        )}

                        {/* Content */}
                        <div
                            className={styles.dialogContent}
                            style={{
                                ...(height && { maxHeight: 'none' }),
                                ...contentStyle,
                            }}
                        >
                            {children}
                        </div>

                        {/* Actions */}
                        {actions && <div className={styles.dialogActions}>{actions}</div>}
                    </div>
                </div>
            </div>
        )

        return createPortal(dialogContent, document.body)
    }
)

ScoopDialog.displayName = 'ScoopDialog'
